<template>
  <div class="detalle-inmueble-panel">
    <DxTabPanel height="95%" tabs-position="top" icon-position="left" :defer-rendering="true">
      <DxItem :key="1" :title="$t('inmueble.ui.tabDatos')" class="tab-item" :disabled="false"
        icon="fa-regular fa-house">
        <inmueble-tab-datos :model="model" ref="tabDatos"></inmueble-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="$t('inmueble.ui.tabConsumo')" :visible="isEditMode" class="tab-item" :disabled="false"
        icon="fa-regular fa-bolt">
        <inmueble-tab-consumo v-if="isEditMode" :inmuebleId="(model as EditInmueble).Id" ref="tabConsumo"></inmueble-tab-consumo>
      </DxItem>
      <DxItem :key="3" :title="$t('inmueble.ui.tabInquilinos')" :visible="isEditMode" class="tab-item" :disabled="false" icon="fa-light fa-users">
        <inmueble-tab-inquilinos :inmuebleId="(model as EditInmueble).Id" ref="tabInquilinos" @nuevaVenta="closeModal" ></inmueble-tab-inquilinos>
      </DxItem>
      <DxItem :key="4" :title="$t('inmueble.ui.tabPropietarios')" :visible="isEditMode" class="tab-item"
        :disabled="false" icon="fa-light fa-user-tie">
        <inmueble-tab-propietarios :inmuebleId="(model as EditInmueble).Id" ref="tabPropietarios" @nuevaVenta="closeModal"></inmueble-tab-propietarios>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from 'vue';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import { DxButton } from 'devextreme-vue';
import { useIntl } from '@services/i18n';
import type { CreateInmueble } from '@/components/inmuebles/models/CreateInmueble';
import type { EditInmueble } from '@/components/inmuebles/models/EditInmueble';
import InmuebleTabDatos from '@/components/inmuebles/InmuebleTabDatos.vue';
import InmuebleTabConsumo from '@/components/inmuebles/InmuebleTabConsumo.vue';
import InmuebleTabInquilinos from '@/components/inmuebles/InmuebleTabInquilinos.vue';
import InmuebleTabPropietarios from '@/components/inmuebles/InmuebleTabPropietarios.vue';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { useApi } from '@api/integration';
import { messageBoxResult } from '@components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { primaryButtonProps } from '@/.shared/helpers/button-helper';
import { useRouter } from '@/.shared/services/routing-services';

const intl = useIntl();
const api = useApi();

const props = defineProps({
  model: {
    type: Object as () => CreateInmueble | EditInmueble,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

const tabDatos = ref<InstanceType<typeof InmuebleTabDatos>>();
const tabConsumo = ref<InstanceType<typeof InmuebleTabConsumo>>();
const tabInquilinos = ref<InstanceType<typeof InmuebleTabInquilinos>>();
const tabPropietarios = ref<InstanceType<typeof InmuebleTabPropietarios>>();

const isValid = ref(false);
const modal = ref<ReturnType<typeof useModalBuilder>>();
const router = useRouter();

onMounted(async () => {
  modal.value = useModalBuilder();
  modal.value.setContext(getCurrentInstance()!)

  configureButtons(modal.value);
});

async function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    const id = await saveOrEdit();
    if (id)
      modal.close(messageBoxResult.ok)
  });

  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));

  let nuevaVentaButton: ToolbarReference<DxButton>;
  nuevaVentaButton = modal.addButton('nueva-venta', primaryButtonProps(intl.i18n.t("inmuebles.ui.buttons.nueva_venta")), { toolbar: 'bottom', location: 'before' });
  nuevaVentaButton.update(w => w.icon = 'fa-light fa-plus');
  nuevaVentaButton.update(w => w.onClick = async () => {
    nuevaVenta();
  });
}

const saveOrEdit = async () => {
  if (tabDatos.value?.isValid()) {
    try {
      if (props.isEditMode) {
        await api.callStandardApi("editInmueble", { showLoading: true }, props.model as EditInmueble);
        toastSuccess(intl.i18n.t('inmueble.edicion_exito'));
      } else {
        await api.callStandardApi("createInmueble", { showLoading: true }, props.model as CreateInmueble);
        toastSuccess(intl.i18n.t('inmueble.creacion_exito'));
      }
      return true;
    } catch (error) {
      toastError(props.isEditMode ?
        intl.i18n.t('inmueble.edicion_fallo') :
        intl.i18n.t('inmueble.creacion_fallo')
      );
      return false;
    }
  } else {
    toastError(intl.i18n.t('inmueble.revise_validacion'));
    return false;
  }
}

const nuevaVenta = () => {
  router.push(intl.i18Route({ name: "nueva-venta-inmobiliaria", query: { idInmueble: (props.model as EditInmueble).Id } }));
  closeModal();
}

const closeModal = () => {
  modal.value?.close(messageBoxResult.ok);
}

</script>
